import { useHomeBanner } from '@/hooks/useHomeBanner';
import { BannerSlider } from './components/BannerSlider';
import { PromoMarquee } from '@/components/common/PromoMarquee';
import InstallAppBanner from '@/components/common/InstallAppBanner';
import { CasinoTabs } from './components/CasinoTabs';
import SportsCards from './components/SportsCards';
import SportsSidebar from './components/SportsSidebar';
import HomeCasinoRail from './components/HomeCasinoRail';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFire, FaThumbsUp } from 'react-icons/fa';
import { IoTimeOutline } from 'react-icons/io5';
import { useParlay } from '@/hooks/useParlay';
import { useIsAuthenticated } from '@/hooks/redux';
import { useCasinoGameplay } from '@/hooks/useCasinoGameplay';
import { useGetCasinoGamesQuery, useGetCasinoRecentQuery } from '@/store/api/casinoApi';
import {
  HOT_PREVIEW_VIDEOS,
  isPreviewOnlyGame,
  RECOMMENDED_PREVIEW_VIDEOS,
} from './components/gamePreviewVideos';
import {
  buildHotGames,
  buildRecommendedGames,
  HOME_CURATED_GAME_NAMES,
} from './components/homeGameOrder';
import GameSessionModal from '@/components/common/GameSessionModal';
import Loader from '@/components/common/Loader';
import { WelcomeBanner } from './components/WelcomeBanner';
import type { CasinoGameLite } from '@/types/casino';
import { toast } from 'sonner';

// Supplied banner artwork — each already carries its own name and "View
// All", so it replaces the plain yellow ribbon header for that section.
const HOT_BANNER = '/images/casino-banners/hot.webp';
const RECOMMENDED_BANNER = '/images/casino-banners/recommended.webp';
const HOT_BADGE = '/images/casino-banners/inside-hot.webp';
const RECOMMENDED_BADGE = '/images/casino-banners/inside-recommended.webp';
const RECENT_BANNER = '/images/casino-banners/recent.webp';
const RECENT_BADGE = '/images/casino-banners/inside-recent.webp';

type SportsTab = 'inplay' | 'today' | 'tomorrow';

function HomePage() {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const [activeTab, setActiveTab] = useState<SportsTab>('inplay');
  const { setIsParlayEnabled } = useParlay();
  const { sessionUrl, isModalOpen, sessionLoading, handleGameClick, closeModal } =
    useCasinoGameplay();

  const { data: recent, isLoading: isRecentLoading } = useGetCasinoRecentQuery(undefined, {
    skip: !isAuthenticated,
  });
  // Hot / Recommended follow a fixed, curated order (homeGameOrder.ts).
  // `names` asks the API for exactly these titles in one call — whichever
  // actually exist in the real catalog (e.g. after an aggregator sync) come
  // back as real, launchable games; the rest fall back to "coming soon"
  // cards with the supplied artwork.
  const { data: catalog } = useGetCasinoGamesQuery({ names: HOME_CURATED_GAME_NAMES });
  const hotGames = useMemo(() => buildHotGames(catalog?.games ?? []), [catalog?.games]);
  const recommendedGames = useMemo(
    () => buildRecommendedGames(catalog?.games ?? []),
    [catalog?.games]
  );

  // Parlay mode is a Sports-listing filter, not a site-wide setting — landing
  // back on Home should always show the normal (non-Parlay) view, even if it
  // was left on elsewhere (it's persisted to localStorage so it would
  // otherwise stick).
  useEffect(() => {
    setIsParlayEnabled(false);
  }, [setIsParlayEnabled]);
  const {
    showWelcomeBanner,
    setShowWelcomeBanner,
    displayHomeBanner,
    displayWelcomeBanner,
    isBannersLoading,
  } = useHomeBanner();

  const hasWelcomeBanner = useMemo(() => {
    return displayWelcomeBanner.length > 0;
  }, [displayWelcomeBanner]);

  const handlePreviewAwareClick = (game: CasinoGameLite) => {
    if (isPreviewOnlyGame(game.id)) {
      toast.info(`${game.name} is coming soon.`);
      return;
    }
    handleGameClick(game);
  };

  return (
    <div className="text-white">
      <BannerSlider
        banners={displayHomeBanner}
        className={'aspect-[7.5/2]'}
        isLoading={isBannersLoading}
      />
      <PromoMarquee />
      <InstallAppBanner />
      {/* Sports stays right here on Home; every casino category opens the
          casino lobby with that category already selected. */}
      <CasinoTabs
        onChange={(value) => {
          if (value !== 'sports') navigate(`/casino?category=${value}`);
        }}
      />
      {/* Sidebar column kept just wide enough for its icons/labels — the
          narrower it is, the more width (and, since the cards' aspect ratio
          is fixed to their art, height) the cards column gets. */}
      <div className="grid grid-cols-[3.75rem_1fr] gap-1.5 bg-gradient-to-b from-[#ffe94d] via-[#fff5b8] to-[#fffbe8] px-1.5 py-2.5">
        <SportsSidebar
          activeTab={activeTab}
          onTabChange={(tab) => {
            if (tab === 'inplay' || tab === 'today' || tab === 'tomorrow') setActiveTab(tab);
          }}
        />

        <SportsCards type={activeTab} />
      </div>

      {/* Home teases up to 10 of each, 2 rows deep — the title's own ">"
          opens the full list in the casino lobby, already on this
          section's category/view. */}
      <div className="space-y-3 pb-3 px-2">
        <HomeCasinoRail
          title="Hot"
          bannerSrc={HOT_BANNER}
          badgeSrc={HOT_BADGE}
          icon={FaFire}
          games={hotGames.slice(0, 16)}
          rows={2}
          isLoading={false}
          onGameClick={handlePreviewAwareClick}
          href="/casino?category=HOT"
          previewVideos={HOT_PREVIEW_VIDEOS}
        />

        {/* No dedicated recommendation endpoint — this is the platform-wide
            most-played list standing in for it. */}
        <HomeCasinoRail
          title="Recommended"
          bannerSrc={RECOMMENDED_BANNER}
          badgeSrc={RECOMMENDED_BADGE}
          icon={FaThumbsUp}
          games={recommendedGames.slice(0, 16)}
          rows={2}
          isLoading={false}
          onGameClick={handlePreviewAwareClick}
          href="/casino"
          previewVideos={RECOMMENDED_PREVIEW_VIDEOS}
        />

        {isAuthenticated && (
          <HomeCasinoRail
            title="Recent"
            bannerSrc={RECENT_BANNER}
            badgeSrc={RECENT_BADGE}
            icon={IoTimeOutline}
            games={(recent?.games ?? []).slice(0, 10).map((row) => row.game)}
            isLoading={isRecentLoading}
            onGameClick={handleGameClick}
            href="/casino?view=recent"
          />
        )}
      </div>

      {sessionLoading && (
        <Loader className="fixed z-[70] flex items-center justify-center bg-black/50" />
      )}
      <GameSessionModal url={sessionUrl} isOpen={isModalOpen} onClose={closeModal} />

      {showWelcomeBanner && hasWelcomeBanner && (
        <WelcomeBanner
          setShowWelcomeBanner={setShowWelcomeBanner}
          banners={displayWelcomeBanner}
          isLoading={isBannersLoading}
        />
      )}
    </div>
  );
}

export default HomePage;
